// "@" mentions in the composer. The draft shows "@Name" while typing; on
// send each remembered name becomes the reference token the agent uses, so
// the bubble renders the same badge as a token in Phoebe's replies.
import {
    parseReferenceTokens,
    type ReferenceType,
} from '@apps/web/features/phoebe_chat_v3/model/reference_tokens';

export type MentionRange = Readonly<{
    /** Index of the "@" in the draft. */
    readonly start: number;
    /** Caret position, one past the typed query. */
    readonly end: number;
    readonly query: string;
}>;

export type MentionToken = Readonly<{
    readonly label: string;
    readonly token: string;
    readonly type: ReferenceType;
}>;

// A settled empty search closes an abandoned multi-word mention in the UI,
// so spaces can remain part of the query without following the whole draft.
const MENTION_AT_CARET = /(?:^|\s)@([^@\n]{0,40})$/u;

export function mentionQueryAt(
    text: string,
    caret: number,
): MentionRange | null {
    const match = MENTION_AT_CARET.exec(text.slice(0, caret));
    if (match === null || match[1] === undefined) {
        return null;
    }
    return { start: caret - match[1].length - 1, end: caret, query: match[1] };
}

export function insertMention(
    text: string,
    range: MentionRange,
    label: string,
): Readonly<{ readonly text: string; readonly caret: number }> {
    const mention = `@${label} `;
    return {
        text: text.slice(0, range.start) + mention + text.slice(range.end),
        caret: range.start + mention.length,
    };
}

export function withMentionTokens(
    text: string,
    mentions: ReadonlyArray<MentionToken>,
): string {
    // Longest names first so "@Adrian Yang" is never eaten by "@Adrian".
    const ordered = [...mentions].sort(
        (left, right) => right.label.length - left.label.length,
    );
    let result = text;
    for (const mention of ordered) {
        // A name is only a mention when nothing letter-like follows it, so
        // "@Janet" survives a picked "@Jane".
        const escaped = mention.label.replace(/[.*+?^${}()|[\]\\]/gu, '\\$&');
        result = result.replace(
            new RegExp(`@${escaped}(?![\\p{L}\\p{N}])`, 'gu'),
            mention.token,
        );
    }
    return result;
}

export type MentionSegment =
    | Readonly<{ readonly kind: 'text'; readonly text: string }>
    | Readonly<{
          readonly kind: 'mention';
          readonly text: string;
          readonly type: ReferenceType;
          /** The referenced record's id, from the mention's token. */
          readonly id: string;
      }>;

function mentionTokenId(token: string): string {
    const [segment] = parseReferenceTokens(token);
    return segment !== undefined && segment.kind === 'reference'
        ? segment.id
        : '';
}

/** Splits a draft so the composer can badge the mentions it remembers. */
export function mentionSegments(
    text: string,
    mentions: ReadonlyArray<MentionToken>,
): ReadonlyArray<MentionSegment> {
    if (mentions.length === 0 || text === '') {
        return text === '' ? [] : [{ kind: 'text', text }];
    }
    const ordered = [...mentions].sort(
        (left, right) => right.label.length - left.label.length,
    );
    const segments: Array<MentionSegment> = [];
    let index = 0;
    while (index < text.length) {
        const mention = ordered.find((candidate) =>
            text.startsWith(`@${candidate.label}`, index),
        );
        if (mention === undefined) {
            const last = segments[segments.length - 1];
            if (last !== undefined && last.kind === 'text') {
                segments[segments.length - 1] = {
                    kind: 'text',
                    text: last.text + text[index],
                };
            } else {
                segments.push({ kind: 'text', text: text[index] ?? '' });
            }
            index += 1;
            continue;
        }
        segments.push({
            kind: 'mention',
            text: `@${mention.label}`,
            type: mention.type,
            id: mentionTokenId(mention.token),
        });
        index += mention.label.length + 1;
    }
    return segments;
}

/**
 * Labels double as the mention key inside the draft, so two records that
 * share a name need distinct labels or the second pick would silently
 * take over the first one's references.
 */
export function uniqueMentionLabel(
    label: string,
    detail: string | null,
    mentions: ReadonlyArray<MentionToken>,
    token: string,
): string {
    function taken(candidate: string): boolean {
        return mentions.some(
            (item) => item.label === candidate && item.token !== token,
        );
    }
    if (!taken(label)) {
        return label;
    }
    if (detail !== null && !taken(`${label} (${detail})`)) {
        return `${label} (${detail})`;
    }
    let index = 2;
    while (taken(`${label} ${index}`)) {
        index += 1;
    }
    return `${label} ${index}`;
}
